import AddIcon from "@mui/icons-material/Add";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import { useState } from "react";
import { useAdminThankYou } from "../../hooks/useAdminThankYou";

export default function ThankYouFeed() {
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"masonry" | "list">("masonry");
  const { data: messages, isLoading, error } = useAdminThankYou({ search });

  const handleExport = () => {
    if (!messages || messages.length === 0) return;
    const csvContent =
      "data:text/csv;charset=utf-8," +
      ["Date,Campaign,Organization,Recipient,Message"]
        .concat(
          messages.map(
            (m) =>
              `"${m.date}","${m.campaignName}","${m.orgName}","${m.recipientName}","${m.message.replace(/"/g, '""')}"`,
          ),
        )
        .join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "thank-you-messages.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const count = messages?.length ?? 0;

  return (
    <div className="mt-[32px]">
      {/* Head */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-[14px]">
        <div className="flex items-center gap-[10px]">
          <FavoriteRoundedIcon className="text-dark-dust" style={{ fontSize: "22px" }} />
          <h3 className="font-crimson text-[22px] font-normal text-rodeo-black m-0">
            Thank-you messages
          </h3>
          <span className="text-[13px] text-graphite">
            {count} message{count !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* Search bar */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap w-full min-h-[40px] box-border border border-smoke rounded-[10px] p-[4px_12px] bg-white focus-within:border-dark-dust transition-colors">
            <SearchIcon className="text-graphite shrink-0" style={{ fontSize: "20px" }} />
            <input
              type="text"
              placeholder="Search and filter thank-you messages"
              className="flex-1 min-w-[160px] border-none outline-none bg-transparent font-lato text-[14px] text-rodeo-black py-1 placeholder:text-graphite"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search thank-you messages"
            />
            <button
              type="button"
              className="inline-flex items-center gap-1 bg-transparent border border-dashed border-rodeo-dust text-rodeo-black rounded-full py-[3px] px-[12px] pl-[9px] font-lato text-[13px] font-bold cursor-pointer whitespace-nowrap hover:bg-light-dust transition-colors"
            >
              <AddIcon style={{ fontSize: "17px" }} /> Add filter
            </button>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-[10px] shrink-0">
          {/* Favorites toggle (visual only for now) */}
          <button
            type="button"
            className="inline-flex items-center gap-[6px] h-10 box-border border border-smoke bg-white rounded-lg py-[7px] px-[12px] font-lato text-[13px] font-bold text-graphite cursor-pointer transition-all hover:border-dark-dust"
            title="Show favorites"
          >
            <FavoriteBorderIcon className="text-rodeo-dust" style={{ fontSize: "17px" }} />{" "}
            Favorites
          </button>

          {/* View toggle */}
          <div className="inline-flex h-10 box-border border border-smoke rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("masonry")}
              className={`inline-flex items-center justify-center h-full border-none py-[7px] px-[10px] cursor-pointer ${viewMode === "masonry" ? "bg-dark-dust text-white" : "bg-white text-graphite"}`}
              title="Card view"
              aria-label="Card view"
            >
              <GridViewRoundedIcon style={{ fontSize: "18px" }} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center justify-center h-full border-none border-l border-smoke py-[7px] px-[10px] cursor-pointer ${viewMode === "list" ? "bg-dark-dust text-white" : "bg-white text-graphite"}`}
              title="List view"
              aria-label="List view"
            >
              <ViewListRoundedIcon style={{ fontSize: "18px" }} />
            </button>
          </div>

          {/* Export CSV */}
          <button
            type="button"
            onClick={handleExport}
            disabled={!messages || messages.length === 0}
            className="inline-flex items-center gap-2 h-10 box-border bg-white text-rodeo-black border border-dark-dust py-[11px] px-[20px] rounded-[6px] font-lato text-[11px] font-bold uppercase tracking-[1.5px] cursor-pointer transition-colors hover:bg-wax disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <FileDownloadOutlinedIcon style={{ fontSize: "18px" }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="animate-pulse h-64 bg-smoke rounded-lg" />
      ) : error ? (
        <div className="p-4 text-good-wine bg-good-wine-bg rounded-lg">Error loading messages.</div>
      ) : !messages || messages.length === 0 ? (
        <div className="py-2 px-0.5">
          <div className="text-graphite text-[14px]">
            No thank-you messages match these filters.
          </div>
        </div>
      ) : viewMode === "masonry" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {messages.map((msg) => (
            <div key={msg.id} className="w-full">
              <div
                className="relative border border-smoke rounded-[12px] bg-white p-[18px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] box-border flex flex-col"
                style={{ aspectRatio: "1 / 1" }}
              >
                {/* Card top: heart + info (visual only) */}
                <div className="flex justify-end gap-1 mb-[6px]">
                  <button
                    type="button"
                    className="border-none bg-transparent cursor-pointer text-rodeo-dust inline-flex p-[3px] rounded-md hover:text-rodeo-black hover:bg-wax transition-colors"
                    title="Favorite"
                  >
                    <FavoriteBorderIcon style={{ fontSize: "19px" }} />
                  </button>
                  <button
                    type="button"
                    className="border-none bg-transparent cursor-pointer text-rodeo-dust inline-flex p-[3px] rounded-md hover:text-dark-dust hover:bg-wax transition-colors"
                    title="Details"
                  >
                    <InfoOutlinedIcon style={{ fontSize: "19px" }} />
                  </button>
                </div>
                {/* Quote — vertically centered, fills remaining space */}
                <div className="font-crimson text-[20px] leading-[1.45] text-rodeo-black flex-1 flex items-center py-2 px-4">
                  &ldquo;{msg.message}&rdquo;
                </div>
                {/* Recipient pinned to bottom */}
                <div className="mt-3 font-bold text-[14px] text-rodeo-black">
                  {msg.recipientName}
                </div>
                <div className="text-[12px] text-graphite mt-[2px]">
                  {msg.orgName} · {new Date(msg.date).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List view */
        <div className="flex flex-col gap-[10px]">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className="flex items-center gap-[14px] border border-smoke rounded-[10px] bg-white py-3 px-4"
            >
              <div className="w-[48px] h-[48px] shrink-0 rounded-lg bg-wax overflow-hidden flex items-center justify-center">
                <CardGiftcardIcon className="text-rodeo-dust" style={{ fontSize: "24px" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-crimson text-[15px] text-rodeo-black leading-[1.4]">
                  &ldquo;{msg.message}&rdquo;
                </div>
                <div className="text-[12px] text-graphite mt-[3px]">
                  {msg.recipientName} · {msg.orgName} · {new Date(msg.date).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                className="border-none bg-transparent cursor-pointer text-rodeo-dust inline-flex p-[3px] rounded-md hover:text-rodeo-black hover:bg-wax transition-colors"
                title="Favorite"
              >
                <FavoriteBorderIcon style={{ fontSize: "19px" }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
